import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Text, ActionIcon, Button } from '@repletoryreach/sdk-ui';
import { Drawer, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { useT } from '../../../../i18n/I18nProvider';
import { StatusPill } from '../../../../components/panel';
import { FlowDrawer } from '../../index';
import { ENTRY_KINDS, triggerType, type EntryKind } from '../../entry-templates';

/**
 * The flows, from inside the builder: name, description, whether it is on,
 * and for the entry points which one it is. A click opens that flow here
 * (asking first when the current one has unsaved changes); the gear opens
 * its settings.
 */
export function FlowsDrawer({ opened, onClose, currentId, dirty }: {
  opened: boolean; onClose: () => void; currentId: string; dirty: boolean;
}) {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<flows.Flow | 'new' | null>(null);
  const list = useQuery({ queryKey: ['flows'], queryFn: () => flows.listFlows(), enabled: opened });
  const all = list.data?.data ?? [];
  const { entries, others } = useMemo(() => {
    const entries: Array<{ kind: EntryKind; flow: flows.Flow }> = [];
    const seen = new Set<string>();
    for (const kind of ENTRY_KINDS) {
      const f = all.find((x) => triggerType(x.definition) === kind);
      if (f) { entries.push({ kind, flow: f }); seen.add(f.id); }
    }
    const others = all.filter((f) => !seen.has(f.id)).sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));
    return { entries, others };
  }, [all]);

  const open = (f: flows.Flow) => {
    if (f.id === currentId) { onClose(); return; }
    const go = () => { onClose(); nav(`/flows/${f.id}/builder`); };
    if (!dirty) { go(); return; }
    openConfirmModal({
      title: t('flows.builder.flowsDrawer.switchTitle'),
      children: <Text role="bodySmall">{t('flows.builder.flowsDrawer.switchBody')}</Text>,
      labels: { confirm: t('flows.builder.flowsDrawer.switchConfirm'), cancel: t('flows.builder.discard.keep') },
      confirmProps: { color: 'red' },
      onConfirm: go
    });
  };

  const row = (f: flows.Flow, kind?: EntryKind) => (
    <div key={f.id} className={`rr-flows-drawer__row${f.id === currentId ? ' is-current' : ''}`}>
      <button type="button" className="rr-flows-drawer__open" onClick={() => open(f)}>
        <span className="rr-flows-drawer__name">
          {f.name}
          <StatusPill tone={f.is_active ? 'active' : 'paused'}>{f.is_active ? t('status.active') : t('status.inactive')}</StatusPill>
          {f.id === currentId && <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.builder.flowsDrawer.current')}</Text>}
        </span>
        {kind && t(`flows.entry.${kind}.name`) !== f.name && <Text role="caption" c="var(--rr-color-text-muted)">{t(`flows.entry.${kind}.name`)}</Text>}
        <span className="rr-flows-drawer__desc">{f.description || t('flows.builder.flowsDrawer.noDescription')}</span>
      </button>
      <Tooltip label={t('flows.ws.configure')} position="left">
        <ActionIcon variant="subtle" size="md" aria-label={t('flows.ws.configure')} onClick={() => setEditing(f)}><GearIcon /></ActionIcon>
      </Tooltip>
    </div>
  );

  return (
    <>
      <Drawer opened={opened} onClose={onClose} title={t('flows.builder.flowsDrawer.title')} position="right" size="md">
        <div className="rr-flows-drawer">
          <div className="rr-flows-drawer__top">
            <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.builder.flowsDrawer.hint')}</Text>
            <Button size="xs" onClick={() => setEditing('new')}>{t('flows.actions.create')}</Button>
          </div>
          {entries.length > 0 && <div className="rr-flows-drawer__group">{t('flows.ws.entryPoints')}</div>}
          {entries.map(({ kind, flow }) => row(flow, kind))}
          <div className="rr-flows-drawer__group">{t('flows.ws.yourFlows')}</div>
          {others.map((f) => row(f))}
          {!list.isPending && others.length === 0 && <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.ws.none')}</Text>}
        </div>
      </Drawer>
      <FlowDrawer
        flow={editing}
        cloneFrom={null}
        onClose={() => setEditing(null)}
        onSaved={() => { qc.invalidateQueries({ queryKey: ['flows'] }); qc.invalidateQueries({ queryKey: ['flow', currentId] }); }}
      />
    </>
  );
}

function GearIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="8" cy="8" r="2.2" />
      <path d="M8 1.8v1.6M8 12.6v1.6M1.8 8h1.6M12.6 8h1.6M3.6 3.6l1.1 1.1M11.3 11.3l1.1 1.1M3.6 12.4l1.1-1.1M11.3 4.7l1.1-1.1" />
    </svg>
  );
}
